import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../context/ThemeContext';
import { X, Share, Plus, Bell, Smartphone } from 'lucide-react';

export default function InstallPWAModal({ isOpen, onClose }) {
  const { isDark } = useTheme();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const steps = [
    { icon: Share, text: "Tocca Condividi", highlight: true },
    { icon: Plus, text: "Aggiungi alla Home" },
    { icon: Smartphone, text: "Apri dalla Home" },
    { icon: Bell, text: "Attiva notifiche" },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[200]" data-testid="install-pwa-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - compact bottom sheet */}
      <div className={`absolute bottom-0 left-0 right-0 rounded-t-2xl ${
        isDark ? 'bg-[#0A0F1C]' : 'bg-white'
      }`}>
        {/* Handle bar */}
        <div className="flex justify-center pt-2">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        </div>

        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E8334A] to-[#EC4899] flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Installa Slimmy
              </h2>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Per ricevere le notifiche
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isDark ? 'bg-white/10' : 'bg-gray-100'
            }`}
          >
            <X className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-600'}`} />
          </button>
        </div>

        {/* Steps - horizontal compact */}
        <div className="px-5 py-3">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.label || `step-${index}`} className="flex flex-col items-center gap-1.5 flex-1">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                    step.highlight 
                      ? 'bg-[#E8334A] text-white' 
                      : isDark 
                        ? 'bg-white/10 text-white/70' 
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-[10px] text-center leading-tight ${
                    step.highlight 
                      ? isDark ? 'text-[#E8334A]' : 'text-[#E8334A]'
                      : isDark ? 'text-white/60' : 'text-gray-500'
                  }`}>
                    {step.text}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`absolute mt-5 ml-14 w-4 h-[2px] ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} 
                      style={{ display: 'none' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action button */}
        <div className="px-5 pb-5 pt-2">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-full font-semibold bg-[#E8334A] text-white text-sm"
          >
            Ho capito
          </button>
        </div>

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>
    </div>,
    document.body
  );
}
